import { useCallback, useEffect, useRef } from 'react';
import { DraggableData } from 'react-draggable';
import { getMaxBounds } from '../../../../utils/commonFunctions';

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

interface UseModalInteractionProps {
    draggable?: boolean;
    resizable?: boolean;
    pinned?: boolean;
    minWidth?: number;
    minHeight?: number;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    onResizeStart?: () => void;
    onResizeEnd?: () => void;
}

export const useModalInteraction = ({
    draggable = true,
    resizable = false,
    pinned = false,
    minWidth = 200,
    minHeight = 100,
    onDragStart,
    onDragEnd,
    onResizeStart,
    onResizeEnd,
}: UseModalInteractionProps = {}) => {

    const modalRef = useRef<HTMLDivElement>(null);

    /** ================= STATE FLAGS ================= */
    const isResizing = useRef(false);
    const dragOffsetX = useRef(0);
    const dragOffsetY = useRef(0);

    /** ================= DRAG ================= */

    const onDragStartInternal = useCallback(
        (e: any, data: DraggableData): void | false => {
            if (!draggable || pinned || isResizing.current) return false;

            if (modalRef.current) {
                modalRef.current.style.transition = 'none';
                const rect = modalRef.current.getBoundingClientRect();
                dragOffsetX.current = e.clientX - rect.left;
                dragOffsetY.current = e.clientY - rect.top;
            }

            document.body.style.userSelect = 'none';
            onDragStart?.();
        },
        [draggable, pinned, onDragStart]
    );

    const onDragInternal = useCallback((e: any) => {
        if (!modalRef.current || isResizing.current) return;
        modalRef.current.style.transition = 'none';
        modalRef.current.style.left = `${e.clientX - dragOffsetX.current}px`;
        modalRef.current.style.top = `${e.clientY - dragOffsetY.current}px`;
    }, []);

    const onDragStopInternal = useCallback(() => {
        if (modalRef.current) {
            modalRef.current.style.transition = 'all 0.3s ease';
        }
        document.body.style.userSelect = 'auto';
        onDragEnd?.();
    }, [onDragEnd]);

    /** ================= RESIZE ================= */

    const startX = useRef(0);
    const startY = useRef(0);
    const startWidth = useRef(0);
    const startHeight = useRef(0);
    const startTop = useRef(0);
    const startLeft = useRef(0);
    const resizingHandle = useRef<ResizeHandle | null>(null);

    const { width: maxWidth, height: maxHeight } = getMaxBounds();

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!resizingHandle.current || !modalRef.current) return;

        const deltaX = e.clientX - startX.current;
        const deltaY = e.clientY - startY.current;
        const handle = resizingHandle.current;

        let newWidth = startWidth.current;
        let newHeight = startHeight.current;
        let newTop = startTop.current;
        let newLeft = startLeft.current;

        // Prevent text selection during resize
        document.body.style.userSelect = 'none';

        // Handle corner and edge resizing
        if (handle.includes('e')) {
            // Right edge
            newWidth = Math.max(minWidth, Math.min(startWidth.current + deltaX, maxWidth));
        }
        if (handle.includes('w')) {
            // Left edge
            const resizeDelta = Math.min(startWidth.current - minWidth, deltaX);
            newLeft = Math.max(startLeft.current + resizeDelta);
            newWidth = startWidth.current - resizeDelta;
        }
        if (handle.includes('s')) {
            // Bottom edge
            newHeight = Math.max(minHeight, Math.min(startHeight.current + deltaY, maxHeight));
        }
        if (handle.includes('n')) {
            // Top edge
            const resizeDelta = Math.min(startHeight.current - minHeight, deltaY);
            newTop = startTop.current + resizeDelta;
            newHeight = startHeight.current - resizeDelta;
        }

        // Apply new dimensions and position
        modalRef.current.style.width = `${newWidth}px`;
        modalRef.current.style.height = `${newHeight}px`;
        modalRef.current.style.top = `${newTop}px`;
        modalRef.current.style.left = `${newLeft}px`;

    }, [minWidth, minHeight, maxWidth, maxHeight]);

    const handleMouseUp = useCallback(() => {
        resizingHandle.current = null;
        document.body.style.userSelect = 'auto';
        onResizeEnd?.();

        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove, onResizeEnd]);

    const startResize = useCallback((handle: ResizeHandle) => {
        return (e: React.MouseEvent<HTMLDivElement>) => {
            if (!modalRef.current || !resizable) return;
            e.preventDefault();
            e.stopPropagation();

            startX.current = e.clientX;
            startY.current = e.clientY;
            startWidth.current = modalRef.current.offsetWidth;
            startHeight.current = modalRef.current.offsetHeight;
            startTop.current = modalRef.current.offsetTop;
            startLeft.current = modalRef.current.offsetLeft;
            resizingHandle.current = handle;

            onResizeStart?.();

            window.addEventListener('mousemove', handleMouseMove, { passive: false });
            window.addEventListener('mouseup', handleMouseUp, { passive: true });
        };
    }, [handleMouseMove, handleMouseUp, onResizeStart, modalRef.current]);

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    return {
        modalRef,
        dragHandlers: {
            onStart: onDragStartInternal,
            onDrag: onDragInternal,
            onStop: onDragStopInternal,
        },
        startResize,
    };
};

export default useModalInteraction;
